import { useEffect, useState, type MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { twJoin } from "tailwind-merge";

import type { EventPollVote } from "@/api/bale.zod";

import { useGetProfile, useUpdateEventPollVotes } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { Card, CardActionsHeader, CardTextContent } from "@/components/Card";
import { DragHandle } from "@/components/drag-columns/components/DragItem";
import { Icon } from "@/components/Icon";
import { IconButton } from "@/components/IconButton";
import { Missing } from "@/components/Missing";
import { TextContent } from "@/components/TextContent";
import { setEq } from "@/fp/setEq";
import { useCurrentEventSuspense } from "@/pages/EventPage/hooks/useCurrentEvent";
import { useEventWidgets } from "@/pages/EventPage/hooks/useEventWidgets";

import type { PollWidget } from "./PollCard";

export function StaticPollCard({ widget, onEdit }: { widget: PollWidget; onEdit?: () => void }) {
  const { t } = useTranslation();
  const { replaceWidget } = useEventWidgets();

  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });
  const me = useGetProfile("@me", { query: { select: (data) => data.data.profile } });
  const updateEventPollVotes = useUpdateEventPollVotes({
    mutation: {
      onSuccess: (response) => {
        replaceWidget(widget.id, { ...widget, ...response.data.event_poll });
        if (me.data) {
          setIsResultVisibleWanted(response.data.event_poll.votes.some((vote) => vote.account_id === me.data.id));
        }
      },
    },
  });

  const isAttending = event.data.event_participants.some(
    (participant) => participant.profile.id === me.data?.id && participant.response === "Attending",
  );

  const myVotes = new Set(
    me.data && widget.votes?.filter((vote) => vote.account_id === me.data?.id).map((vote) => vote.option_id),
  );
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const isVoted = myVotes.size > 0;
  const isVotable = isAttending && (widget.is_response_editable || !isVoted);

  const [isResultVisibleWanted, setIsResultVisibleWanted] = useState(isVoted);
  const isResultVisibleAllowed = !!widget.votes?.length;
  const isResultVisible = isResultVisibleWanted && isResultVisibleAllowed;

  useEffect(() => {
    setSelection(new Set(myVotes)); // oxlint-disable-line set-state-in-effect
    setIsResultVisibleWanted(isVoted); // oxlint-disable-line exhaustive-effect-dependencies
  }, [isAttending]);

  const maxVotes = Math.max(
    // NOTE: saw it crash due to `.values().map()` is not a function; weird cause this should be supported by now
    // but I'd rather just avoid it than have a polyfill. Maybe undo this someday?
    ...Array.from(Map.groupBy(widget.votes ?? [], (vote) => vote.option_id).values(), (votes) => votes.length),
  );

  function toggleSelection(optionId: number) {
    setSelection((selection) => {
      selection = new Set(selection); // oxlint-disable-line no-param-reassign
      if (selection.has(optionId)) {
        selection.delete(optionId);
      } else if (widget.max_selections === 1) {
        selection.clear();
        selection.add(optionId);
      } else if (!widget.max_selections || selection.size < widget.max_selections) {
        selection.add(optionId);
      }
      return selection;
    });
  }

  function saveVote() {
    updateEventPollVotes.mutate({ widgetId: widget.id, data: { votes: Array.from(selection) } });
  }

  return (
    <Card>
      <DragHandle>
        <CardActionsHeader title={t("event-poll")}>
          {!!onEdit && <IconButton type="button" name="settings" onClick={onEdit} aria-label={t("edit-poll")} />}
        </CardActionsHeader>
      </DragHandle>
      <CardTextContent className="flex flex-col gap-4">
        <TextContent content={widget.subject} />{" "}
        <div className="flex flex-col gap-2 rounded-sm border border-divider bg-black/2 p-2">
          {widget.options?.map((option) => (
            <PollOption
              key={option.id}
              title={option.title}
              isVoted={myVotes.has(option.id)}
              isSelected={selection.has(option.id)}
              votes={isResultVisible ? widget.votes?.filter((vote) => vote.option_id === option.id) : undefined}
              maxVotes={maxVotes}
              onClick={isVotable ? () => toggleSelection(option.id) : undefined}
            />
          ))}
          {!widget.options?.length && <Missing>{t("poll-no-options")}</Missing>}
        </div>
        <ActionBox className="ml-auto">
          <Button
            type="button"
            disabled={!isResultVisibleAllowed}
            onClick={() => setIsResultVisibleWanted((is) => !is)}
          >
            {isResultVisible && t("hide-results")}
            {isResultVisibleAllowed && !isResultVisible && t("show-results")}
            {!isResultVisibleAllowed && t("vote-to-see-results")}
          </Button>
          <Button
            type="button"
            attention="submit"
            disabled={!isVotable || setEq(selection, myVotes)}
            onClick={saveVote}
          >
            {isVoted && isVotable && t("update-votes")}
            {!isVoted && isVotable && t("cast-votes")}
            {isVoted && !isVotable && t("already-voted")}
            {!isVoted && !isVotable && t("you-may-not-vote")}
          </Button>
        </ActionBox>
      </CardTextContent>
    </Card>
  );
}

function PollOption({
  title,
  isVoted,
  isSelected,
  votes,
  maxVotes,
  onClick,
}: {
  title: string;
  isVoted: boolean;
  isSelected: boolean;
  votes?: EventPollVote[];
  maxVotes: number;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}) {
  return (
    <div className="flex flex-col rounded-sm bg-white">
      <button
        type="button"
        disabled={!onClick}
        onClick={onClick}
        className={twJoin(
          "relative isolate inline-flex h-[2.75rem] items-center justify-center rounded-sm border px-4 font-interest not-disabled:cursor-pointer disabled:cursor-default",
          isVoted && isSelected && "border-water bg-water/40",
          !isVoted && isSelected && "border-grass bg-grass/40",
          isVoted && !isSelected && "border-danger bg-danger/40",
          !isVoted && !isSelected && "border-divider",
        )}
      >
        {!!votes?.length && !!maxVotes && (
          <div
            className={twJoin(
              "absolute inset-y-0 left-0",
              isVoted && isSelected && "bg-water/50",
              !isVoted && isSelected && "bg-grass/50",
              isVoted && !isSelected && "bg-danger/50",
              !isVoted && !isSelected && "bg-black/10",
            )}
            style={{ width: `${(votes.length / maxVotes) * 100}%` }}
          />
        )}
        <div className="relative flex w-full flex-row items-center justify-between gap-4 text-left">
          <div className="grow basis-0 truncate font-interest">{title}</div>
          <div className="flex flex-row items-center gap-4">
            <div className="flex flex-row">
              {votes
                ?.map((vote) => vote.account_id)
                .filter((accountId): accountId is string => !!accountId)
                ?.slice(0, 3)
                .map((accountId) => (
                  <Avatar key={accountId} accountId={accountId} className="-ml-4 size-8 shadow-ortho-sm" />
                ))}
            </div>
            {!!votes?.length && <div className="shrink-0 font-disinterest text-placeholder">{votes.length}</div>}
            {isVoted && isSelected && <Icon name="check" />}
            {isVoted && !isSelected && <Icon name="x" />}
          </div>
        </div>
      </button>
    </div>
  );
}
